import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Select,
  ErrorBanner,
  formatCents,
} from '../components/ui';

interface Product {
  id: string;
  name: string;
  description?: string;
  priceCents: number;
  currency: string;
  type: 'one_time' | 'subscription';
  active: boolean;
  createdAt: string;
}

export function ProductsPage() {
  const [editing, setEditing] = useState<Product | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['products'],
    queryFn: () => api.get<{ data: Product[] }>('/products').catch(() => ({ data: [] })),
  });

  const products = q.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Products"
        subtitle={`${products.length} total`}
        action={<Button onClick={() => setShowCreate(true)}>+ New product</Button>}
      />

      {q.isLoading ? (
        <Spinner />
      ) : products.length === 0 ? (
        <Card>
          <EmptyState
            title="No products yet"
            description="Add the things you sell. Products show up on invoices, public pages, and checkout."
            action={<Button onClick={() => setShowCreate(true)}>+ New product</Button>}
          />
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--space-3)' }}>
          {products.map((p) => (
            <Card key={p.id} onClick={() => setEditing(p)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 'var(--space-2)' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 600 }}>{p.name}</h3>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                    {p.type === 'subscription' ? 'Subscription' : 'One-time'}
                  </div>
                </div>
                <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--color-brand-primary)' }}>
                  {formatCents(p.priceCents, p.currency)}
                </div>
              </div>
              {p.description && (
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{p.description}</p>
              )}
            </Card>
          ))}
        </div>
      )}

      {(showCreate || editing) && (
        <ProductModal
          product={editing}
          onClose={() => {
            setShowCreate(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['products'] });
            setShowCreate(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ProductModal({
  product,
  onClose,
  onSaved,
}: {
  product: Product | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(product?.name ?? '');
  const [description, setDescription] = useState(product?.description ?? '');
  const [price, setPrice] = useState(product ? (product.priceCents / 100).toFixed(2) : '0.00');
  const [type, setType] = useState<Product['type']>(product?.type ?? 'one_time');
  const [active, setActive] = useState(product?.active ?? true);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description: description || undefined,
        priceCents: Math.round((Number(price) || 0) * 100),
        currency: product?.currency ?? 'USD',
        type,
        active,
      };
      return product
        ? api.patch(`/products/${product.id}`, body)
        : api.post('/products', body);
    },
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Save failed'),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/products/${product!.id}`),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Delete failed'),
  });

  return (
    <Modal onClose={onClose} title={product ? 'Edit product' : 'New product'}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name) return setError('Name required');
          saveMut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Name *">
          <Input value={name} onChange={setName} autoFocus />
        </Field>
        <Field label="Description">
          <Textarea value={description} onChange={setDescription} rows={3} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
          <Field label="Price">
            <Input value={price} onChange={setPrice} type="number" step="0.01" min={0} />
          </Field>
          <Field label="Type">
            <Select
              value={type}
              onChange={setType}
              options={[
                { value: 'one_time', label: 'One-time' },
                { value: 'subscription', label: 'Subscription' },
              ]}
            />
          </Field>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active
        </label>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'var(--space-2)' }}>
          {product ? (
            <Button variant="danger" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>
              {deleteMut.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
